'use client'

import { useMemo, useState } from 'react'

import { Clock } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover'
import { ScrollArea } from '@/components/ui/scroll-area'

import { cn, isFieldInvalid } from '@/utils'

import { FieldWrapper, FieldWrapperClasses } from './field-wrapper'
import { useFieldContext } from './form'

type DurationParts = {
  hours: number
  minutes: number
}

const HOURS_24 = Array.from({ length: 24 }, (_value, index) => index)
const MINUTES_60 = Array.from({ length: 60 }, (_value, index) => index)

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max)

const pad = (value: number) => value.toString().padStart(2, '0')

// Convert minutes to hours and minutes
const minutesToDuration = (
  totalMinutes: null | number | undefined
): DurationParts => {
  if (totalMinutes == null || totalMinutes < 0) {
    return { hours: 0, minutes: 0 }
  }
  return {
    hours: Math.floor(totalMinutes / 60),
    minutes: totalMinutes % 60
  }
}

// Convert hours and minutes to total minutes
const durationToMinutes = ({ hours, minutes }: DurationParts): number => {
  return hours * 60 + minutes
}

// Format duration for display (HH:MM)
const formatDisplay = ({ hours, minutes }: DurationParts): string => {
  return `${pad(hours)}:${pad(minutes)}`
}

type PickerColumnProps = {
  label: string
  onSelect: (value: number) => void
  options: number[]
  selected?: number
}

const PickerColumn = ({
  label,
  onSelect,
  options,
  selected
}: PickerColumnProps) => {
  return (
    <div className="flex min-w-[96px] flex-1 flex-col">
      <div className="border-border/80 text-muted-foreground/80 border-b px-3 py-2 text-xs font-medium tracking-wide uppercase">
        {label}
      </div>
      <ScrollArea className="h-56">
        <div className="space-y-1 p-2">
          {options.map((option) => {
            const isSelected = option === selected
            return (
              <button
                className={cn(
                  'focus-visible:ring-ring w-full rounded-md px-2 py-1.5 text-start text-sm font-medium transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none',
                  isSelected
                    ? 'bg-primary/10 text-primary'
                    : 'hover:bg-muted hover:text-foreground/90'
                )}
                data-state={isSelected ? 'selected' : undefined}
                key={option}
                onClick={() => onSelect(option)}
                type="button"
              >
                <span className="font-mono">{pad(option)}</span>
              </button>
            )
          })}
        </div>
      </ScrollArea>
    </div>
  )
}

export type DurationPickerProps = {
  classNames?: FieldWrapperClasses
  contentClassName?: string
  disabled?: boolean
  label?: string
  placeholder?: string
  required?: boolean
  triggerClassName?: string
}

export const DurationPicker = ({
  classNames,
  disabled,
  label,
  placeholder = '00:00',
  required = false
}: DurationPickerProps) => {
  const field = useFieldContext<null | number | undefined>()
  const isInvalid = isFieldInvalid(field)
  const [open, setOpen] = useState(false)

  // Convert form value (minutes) to duration parts
  const durationParts = useMemo(
    () => minutesToDuration(field.state.value),
    [field.state.value]
  )

  // Format display value
  const displayValue = useMemo(() => {
    if (field.state.value == null) {
      return placeholder
    }
    return formatDisplay(durationParts)
  }, [durationParts, field.state.value, placeholder])

  const handleCommit = (next: Partial<DurationParts>, closePopover = false) => {
    const merged: DurationParts = {
      hours: clamp(next.hours ?? durationParts.hours, 0, 23),
      minutes: clamp(next.minutes ?? durationParts.minutes, 0, 59)
    }

    const totalMinutes = durationToMinutes(merged)
    field.handleChange(totalMinutes)

    if (closePopover) {
      setOpen(false)
    }
  }

  const handleClear = () => {
    field.handleChange(null)
    setOpen(false)
  }

  return (
    <FieldWrapper
      classNames={classNames}
      field={field}
      label={label}
      required={required}
    >
      <Popover
        onOpenChange={(value) => {
          setOpen(value)
          if (!value) {
            field.handleBlur()
          }
        }}
        open={open}
      >
        <PopoverTrigger asChild>
          <Button
            aria-invalid={isInvalid || undefined}
            className={cn(
              'flex h-[42px] !w-full items-center justify-between gap-3 rounded-md px-3.5',
              field.state.value == null && 'text-muted-foreground'
            )}
            data-invalid={isInvalid || undefined}
            disabled={disabled}
            id={field.name}
            mode="input"
            onBlur={field.handleBlur}
            type="button"
            variant="outline"
            wrapChildren={false}
          >
            <div className="flex items-center gap-2 rtl:flex-row-reverse">
              <Clock className="text-muted-foreground size-4" />
              <span className="font-mono">{displayValue}</span>
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-[320px] max-w-[384px] p-0">
          <div className="divide-border grid grid-cols-2 divide-x border-b">
            <PickerColumn
              label="Hours"
              onSelect={(value) => handleCommit({ hours: value })}
              options={HOURS_24}
              selected={durationParts.hours}
            />
            <PickerColumn
              label="Minutes"
              onSelect={(value) => handleCommit({ minutes: value }, true)}
              options={MINUTES_60}
              selected={durationParts.minutes}
            />
          </div>

          <div className="flex items-center justify-end gap-2 px-3 py-2">
            <Button
              onClick={handleClear}
              size="sm"
              type="button"
              variant="ghost"
            >
              Clear
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </FieldWrapper>
  )
}
